<template>
  <main class="main-content">
    <component :is="NavItemComponent"></component>
    <div class="container">
      <div class="header">
        <div class="left-section">
          <div class="icon-container">
            <img src="/img/icons/module.png" alt="M">
          </div>
          <span class="title">Module Management</span>
        </div>
        <button @click="addNewModule" class="add-button" disabled>
          <img src="/img/icons/add.png" alt="Add icon"/>
          Add new module
        </button>
      </div>
      <div class="divider"></div>

      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>Loading modules...</p>
      </div>

      <div v-else-if="error" class="error-state">
        <p class="error-message-text">
          <img src="/img/icons/error.png" alt="Error" class="error-icon" />
          {{ error }}
        </p>
        <button @click="fetchModules" class="retry-button">Retry</button>
      </div>

      <div v-else-if="modules.length > 0" class="modules-list-container">
        <table class="modules-table">
          <thead>
          <tr>
            <th>Module Name</th>
            <th>Description</th>
            <th>Status</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="module in paginatedModules" :key="module.id">
            <td>{{ module.name }}</td>
            <td>{{ module.description }}</td>
            <td>{{ module.status }}</td>
          </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div v-if="modules.length > 0" class="pagination">
        <div class="rows-per-page">
          <span>Rows per page:</span>
          <select v-model.number="rowsPerPage" @change="onRowsPerPageChange">
            <option :value="10">10</option>
            <option :value="20">20</option>
            <option :value="50">50</option>
            <option :value="100">100</option>
          </select>
        </div>
        <div class="page-info">{{ paginationInfo }}</div>
        <div class="page-controls">
          <button class="first-page" @click="goToFirstPage" :disabled="currentPage === 1" title="First page">
            <span class="arrow-text">⇤</span>
          </button>
          <button class="prev-page" @click="previousPage" :disabled="currentPage === 1" title="Previous page">
            <span class="arrow-text">‹</span>
          </button>
          <span class="page-number">{{ currentPage }} of {{ totalPages }}</span>
          <button class="next-page" @click="nextPage" :disabled="currentPage === totalPages" title="Next page">
            <span class="arrow-text">›</span>
          </button>
          <button class="last-page" @click="goToLastPage" :disabled="currentPage === totalPages" title="Last page">
            <span class="arrow-text">⇥</span>
          </button>
        </div>
      </div>

      <div v-else class="empty-state">
        <div class="empty-icon">
          <img src="/img/icons/bg-settings.png" alt="*" />
        </div>
        <p class="empty-message">
          Currently, there are no available modules. Create a new module using the 'Add new module' button.
        </p>
      </div>
    </div>
  </main>
</template>

<script>
import axios from 'axios';
import NavItem from '@/admin/components/nav/NavItem.vue';

export default {
  name: 'ModuleManagement',
  components: {
    NavItem,
  },
  computed: {
    NavItemComponent() {
      return NavItem;
    },
    paginatedModules() {
      const start = (this.currentPage - 1) * parseInt(this.rowsPerPage, 10);
      const end = start + parseInt(this.rowsPerPage, 10);
      return this.modules.slice(start, end);
    },
    totalPages() {
      return Math.ceil(this.modules.length / parseInt(this.rowsPerPage, 10));
    },
    paginationInfo() {
      if (this.modules.length === 0) {
        return '0 of 0';
      }
      const pageSize = parseInt(this.rowsPerPage, 10);
      const start = (this.currentPage - 1) * pageSize + 1;
      const end = Math.min(this.currentPage * pageSize, this.modules.length);
      return `${start}-${end} of ${this.modules.length}`;
    },
  },
  data() {
    return {
      modules: [],
      loading: false,
      error: null,
      rowsPerPage: 10,
      currentPage: 1,
    };
  },
  async mounted() {
    await this.fetchModules();
  },
  methods: {
    async fetchModules() {
      this.loading = true;
      this.error = null;
      try {
        const response = await axios.get('/lmsagent/login/findSystemModules', {
          headers: {
            Authorization: `Bearer ${this.getAuthToken()}`,
            'Content-Type': 'application/json',
          },
        });

        this.modules = this.transformApiResponse(response.data);
      } catch (err) {
        if (err.response) {
          this.error = `Failed to load modules: ${err.response.statusText} (Status: ${err.response.status})`;
        } else if (err.request) {
          this.error = 'Failed to load modules: No response from server. Please check your network connection.';
        } else {
          this.error = 'Failed to load modules: An unexpected error occurred.';
        }
        this.modules = [];
      } finally {
        this.loading = false;
      }
    },

    transformApiResponse(apiData) {
      let modulesArray = [];

      if (Array.isArray(apiData)) {
        modulesArray = apiData;
      } else if (apiData && Array.isArray(apiData.data)) {
        modulesArray = apiData.data;
      } else if (apiData && Array.isArray(apiData.modules)) {
        modulesArray = apiData.modules;
      } else if (apiData && Array.isArray(apiData.items)) {
        modulesArray = apiData.items;
      }

      if (modulesArray.length > 0) {
        return modulesArray.map((module, index) => ({
          id: module.sysAreaId || module.id || `module-${index}`,
          name: module.sysAreaName || module.name || 'Unnamed Module',
          description: module.sysAreaModule || module.description || 'No description available',
          status: module.status || 'Active',
        }));
      }

      return [];
    },

    getAuthToken() {
      return localStorage.getItem('authToken') || sessionStorage.getItem('authToken') || 'dummy-token-if-not-set';
    },

    addNewModule() {
      this.$emit('change-component', 'RegisterModule');
    },

    goToFirstPage() {
      this.currentPage = 1;
    },
    goToLastPage() {
      this.currentPage = this.totalPages;
    },
    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage -= 1;
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage += 1;
      }
    },
    onRowsPerPageChange() {
      this.currentPage = 1;
    },
  },
};
</script>

<style scoped>
.container {
  max-width: 100%;
  margin: 0 auto;
  padding: 20px;
  background-color: white;
  border-radius: 15px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
}

.left-section {
  display: flex;
  align-items: center;
}

.icon-container {
  width: 40px;
  height: 40px;
  background-color: #0d47a1;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
}

.icon-container img {
  filter: brightness(0) invert(1);
  width: 20px;
  height: 20px;
}

.title {
  font-size: 18px;
  font-weight: 500;
  color: #333;
}

.add-button {
  background-color: #0d47a1;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 10px 15px;
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: background-color 0.3s ease;
}

.add-button:hover:not(:disabled) {
  background-color: #0b3e8f;
}

.add-button:disabled {
  background-color: #ccc;
  color: #666;
  cursor: not-allowed;
  opacity: 0.6;
}

.add-button img {
  margin-right: 8px;
  width: 16px;
  height: 16px;
  filter: brightness(0) invert(1);
}

.divider {
  height: 1px;
  background-color: #f0f0f0;
  margin: 15px 0 25px 0;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 0;
  text-align: center;
}

.empty-icon {
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
}

.empty-icon img {
  max-width: 100%;
  max-height: 100%;
  opacity: 0.7;
}

.empty-message {
  max-width: 350px;
  color: #777;
  font-size: 14px;
  line-height: 1.6;
}

.main-content {
  flex: 1;
  padding: 1.5rem 2rem;
  overflow-y: auto;
  background-color: #f0f2f5;
  left: 16%;
  margin-top: 4%;
  width: 100%;
}

.loading-state, .error-state {
  text-align: center;
  padding: 60px 20px;
  color: #555;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #0d47a1;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-icon {
  width: 24px;
  height: 24px;
  margin-right: 10px;
  vertical-align: middle;
}

.error-message-text {
  display: flex;
  align-items: center;
  color: #d9534f;
  font-size: 16px;
  margin-bottom: 15px;
}

.retry-button {
  background-color: #0d47a1;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 10px 20px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: background-color 0.3s ease;
}

.retry-button:hover {
  background-color: #0b3e8f;
}

.modules-list-container {
  margin-top: 10px;
  overflow-x: auto;
}

.modules-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.03);
  overflow: hidden;
}

.modules-table th, .modules-table td {
  border-bottom: 1px solid #e9ecef;
  padding: 12px 15px;
  text-align: left;
  vertical-align: middle;
}

.modules-table th {
  background-color: #f8f9fa;
  font-weight: 600;
  color: #343a40;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.modules-table tbody tr:hover {
  background-color: #f1f7ff;
}

.modules-table td:first-child, .modules-table th:first-child {
  padding-left: 20px;
}
.modules-table td:last-child, .modules-table th:last-child {
  padding-right: 20px;
}

.pagination {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 20px;
  font-size: 14px;
  color: #666;
}

.rows-per-page {
  display: flex;
  align-items: center;
  margin-right: 24px;
}

.rows-per-page span {
  margin-right: 8px;
}

.rows-per-page select {
  padding: 4px 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: white;
}

.page-info {
  margin-right: 24px;
}

.page-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.page-controls button {
  width: 32px;
  height: 32px;
  border: 1px solid #ddd;
  background-color: white;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #555;
  transition: all 0.2s ease;
}

.page-controls button:hover:not(:disabled) {
  background-color: #f5f5f5;
  border-color: #007bff;
  color: #007bff;
}

.page-controls button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  color: #ccc;
}

.page-controls button .arrow-text {
  font-size: 16px;
  font-weight: bold;
  color: inherit;
  line-height: 1;
}

.page-number {
  padding: 0 12px;
  font-size: 14px;
  color: #555;
  font-weight: 500;
  min-width: 80px;
  text-align: center;
}

</style>
